import { useEffect, useLayoutEffect, useRef, useState } from "react";
import "./NotesSpread.css";

const COLUMN_GAP = 48;
const PAGE_NUMBER_BAND = 28;

function isBlock(node) {
  return node.nodeType === 1 && (node.nodeName === "DIV" || node.nodeName === "P");
}

function plainText(node) {
  if (!node) return "";
  let out = "";
  const walk = (el, atStart) => {
    el.childNodes.forEach((child) => {
      if (child.nodeType === 3) {
        out += String(child.nodeValue || "").replace(/\u00a0/g, " ");
        atStart = false;
      } else if (child.nodeName === "BR") {
        out += "\n";
        atStart = false;
      } else if (child.nodeType === 1) {
        if (isBlock(child) && !atStart) out += "\n";
        atStart = walk(child, true);
      }
    });
    return atStart;
  };
  walk(node, true);
  return out.replace(/\r\n/g, "\n");
}

function caretOffset(root) {
  const selection = window.getSelection();
  if (!selection || !selection.rangeCount) return null;
  const anchor = selection.anchorNode;
  const offset = selection.anchorOffset;
  if (!anchor || !root.contains(anchor)) return null;
  let out = "";
  let found = false;
  const walk = (el, atStart) => {
    for (let index = 0; index < el.childNodes.length; index += 1) {
      if (found) return atStart;
      const child = el.childNodes[index];
      if (el === anchor && index === offset) {
        found = true;
        return atStart;
      }
      if (child === anchor && child.nodeType === 3) {
        out += String(child.nodeValue || "").slice(0, offset).replace(/\u00a0/g, " ");
        found = true;
        return atStart;
      }
      if (child.nodeType === 3) {
        out += String(child.nodeValue || "").replace(/\u00a0/g, " ");
        atStart = false;
      } else if (child.nodeName === "BR") {
        out += "\n";
        atStart = false;
      } else if (child.nodeType === 1) {
        if (isBlock(child) && !atStart) out += "\n";
        atStart = walk(child, true);
      }
    }
    if (el === anchor && offset === el.childNodes.length) found = true;
    return atStart;
  };
  walk(root, true);
  return found ? out.length : null;
}

function setCaret(root, offset) {
  const selection = window.getSelection();
  if (!selection) return;
  let remaining = Math.max(0, offset);
  const place = (node, index) => {
    const range = document.createRange();
    const limit = node.nodeType === 3 ? node.nodeValue.length : node.childNodes.length;
    range.setStart(node, Math.max(0, Math.min(index, limit)));
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  };
  const walk = (el, atStart) => {
    for (let index = 0; index < el.childNodes.length; index += 1) {
      const child = el.childNodes[index];
      if (child.nodeType === 3) {
        const value = String(child.nodeValue || "").replace(/\u00a0/g, " ");
        if (remaining <= value.length) {
          place(child, remaining);
          return true;
        }
        remaining -= value.length;
        atStart = false;
      } else if (child.nodeName === "BR") {
        if (remaining <= 0) {
          place(el, index);
          return true;
        }
        remaining -= 1;
        atStart = false;
      } else if (child.nodeType === 1) {
        if (isBlock(child) && !atStart) {
          if (remaining <= 0) {
            place(el, index);
            return true;
          }
          remaining -= 1;
        }
        if (walk(child, true)) return true;
        atStart = false;
      }
    }
    return false;
  };
  if (!walk(root, true)) place(root, root.childNodes.length);
}

function writeText(el, value) {
  if (plainText(el) === value) return;
  el.textContent = value;
}

function samePages(left, right) {
  if (left.length !== right.length) return false;
  return left.every((page, index) => page === right[index]);
}

function prepareSheet(sheet, pageIndex, slice, titleText) {
  let titleEl = sheet.querySelector(".notes-spread__title");
  let bodyEl = sheet.querySelector(".notes-spread__body");
  if (pageIndex === 0) {
    if (!titleEl) {
      titleEl = document.createElement("div");
      titleEl.className = "notes-spread__title";
      sheet.insertBefore(titleEl, sheet.firstChild);
    }
    titleEl.textContent = titleText;
  } else if (titleEl) {
    titleEl.remove();
  }
  if (!bodyEl) {
    bodyEl = document.createElement("div");
    bodyEl.className = "notes-spread__body";
    sheet.appendChild(bodyEl);
  }
  bodyEl.textContent = slice;
}

function isWordChar(char) {
  return Boolean(char) && !/\s/.test(char);
}

function retreatToWordStart(text, cursor, best) {
  if (best >= text.length || best <= cursor) return best;
  if (!isWordChar(text[best - 1]) || !isWordChar(text[best])) return best;
  let split = best;
  while (split > cursor && isWordChar(text[split - 1])) split -= 1;
  return split > cursor ? split : best;
}

function buildPages(sheet, content, titleText, width, height) {
  const text = String(content || "");
  sheet.style.width = `${width}px`;
  if (!text) {
    prepareSheet(sheet, 0, "", titleText);
    return [""];
  }
  const pages = [];
  let cursor = 0;
  let guard = 0;
  while (cursor < text.length && guard < 400) {
    let low = cursor + 1;
    let high = text.length;
    let best = -1;
    while (low <= high) {
      const mid = (low + high) >> 1;
      prepareSheet(sheet, pages.length, text.slice(cursor, mid), titleText);
      if (sheet.scrollHeight <= height) {
        best = mid;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }
    if (best < 0) best = Math.min(text.length, cursor + 1);
    best = retreatToWordStart(text, cursor, best);
    pages.push(text.slice(cursor, best));
    cursor = best;
    guard += 1;
    if (cursor >= text.length) break;
  }
  if (cursor < text.length) pages.push(text.slice(cursor));
  return pages.length ? pages : [""];
}

export default function NotesSpread({
  title = "",
  content = "",
  editing = false,
  fontSize = 18,
  typeface = "georgia",
  seekPage = null,
  onSeekApplied = () => {},
  onTitleChange = () => {},
  onContentChange = () => {},
  onProgress = () => {},
}) {
  const frameRef = useRef(null);
  const measureRef = useRef(null);
  const titleRef = useRef(null);
  const leftBodyRef = useRef(null);
  const rightBodyRef = useRef(null);
  const leftPageRef = useRef(null);
  const rightPageRef = useRef(null);
  const pagesRef = useRef([""]);
  const spreadRef = useRef(0);
  const pendingCaretRef = useRef(null);
  const editedPageRef = useRef(null);
  const [pages, setPages] = useState([""]);
  const [spread, setSpread] = useState(0);
  const shownTitle = editing ? title : title || "Sans titre";

  const emitPage = (pageIndex, element) => {
    const list = pagesRef.current;
    const before = list.slice(0, pageIndex).join("");
    const after = list.slice(pageIndex + 1).join("");
    onContentChange(before + plainText(element) + after);
    editedPageRef.current = pageIndex;
  };

  useLayoutEffect(() => {
    const frame = frameRef.current;
    const sheet = measureRef.current;
    if (!frame || !sheet) return undefined;

    const layout = () => {
      const width = frame.clientWidth;
      const height = frame.clientHeight;
      if (width < 80 || height < 80) return;
      const pageWidth = Math.floor((width - COLUMN_GAP) / 2);
      const pageHeight = Math.max(40, height - PAGE_NUMBER_BAND);
      const nextPages = buildPages(sheet, content, shownTitle, pageWidth, pageHeight);
      pagesRef.current = nextPages;
      setPages((current) => (samePages(current, nextPages) ? current : nextPages));

      const spreads = Math.max(1, Math.ceil(nextPages.length / 2));
      let spreadIndex = Math.min(spreads - 1, Math.max(0, spreadRef.current));
      const pending = pendingCaretRef.current;
      if (pending) {
        const target = Math.floor(pending.page / 2);
        if (target >= 0 && target < spreads) spreadIndex = target;
      }
      if (spreadIndex !== spreadRef.current) {
        spreadRef.current = spreadIndex;
        setSpread(spreadIndex);
      }

      [leftPageRef.current, rightPageRef.current].forEach((page) => {
        if (!page) return;
        page.style.width = `${pageWidth}px`;
        page.style.height = `${pageHeight}px`;
      });

      const titleNode = titleRef.current;
      if (titleNode && document.activeElement !== titleNode && plainText(titleNode) !== title) {
        titleNode.textContent = title;
      }

      const syncBody = (element, pageIndex) => {
        if (!element) return;
        const slice = nextPages[pageIndex] || "";
        const justEdited = editedPageRef.current === pageIndex && document.activeElement === element;
        if (!justEdited) {
          writeText(element, slice);
          return;
        }
        editedPageRef.current = null;
        if (plainText(element) === slice) return;
        const caret = caretOffset(element);
        writeText(element, slice);
        if (caret == null || caret <= slice.length) {
          if (caret != null) setCaret(element, caret);
          return;
        }
        let page = pageIndex + 1;
        let offset = caret - slice.length;
        while (page < nextPages.length - 1 && offset > (nextPages[page] || "").length) {
          offset -= (nextPages[page] || "").length;
          page += 1;
        }
        pendingCaretRef.current = { page, offset };
      };

      if (editing) {
        syncBody(leftBodyRef.current, spreadIndex * 2);
        syncBody(rightBodyRef.current, spreadIndex * 2 + 1);
        const nextPending = pendingCaretRef.current;
        if (nextPending) {
          const target = Math.max(0, Math.min(spreads - 1, Math.floor(nextPending.page / 2)));
          if (target !== spreadRef.current) {
            spreadRef.current = target;
            setSpread(target);
            return;
          }
          const host = nextPending.page % 2 === 0 ? leftBodyRef.current : rightBodyRef.current;
          if (host) {
            pendingCaretRef.current = null;
            const slice = nextPages[nextPending.page] || "";
            writeText(host, slice);
            host.focus();
            setCaret(host, Math.max(0, Math.min(nextPending.offset, slice.length)));
          }
        }
      }
    };

    layout();
    const observer = new ResizeObserver(layout);
    observer.observe(frame);
    return () => observer.disconnect();
  }, [content, title, shownTitle, editing, fontSize, typeface, spread]);

  useEffect(() => {
    if (!seekPage) return;
    const next = Math.max(0, Math.floor((seekPage - 1) / 2));
    spreadRef.current = next;
    setSpread(next);
    onSeekApplied();
  }, [seekPage, onSeekApplied]);

  useEffect(() => {
    const total = Math.max(1, pages.length);
    const page = Math.min(total, spread * 2 + 1);
    onProgress({ page, total });
  }, [spread, pages, onProgress]);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return undefined;
    let lockedUntil = 0;
    const turn = (direction) => {
      const now = Date.now();
      if (now < lockedUntil) return;
      const spreads = Math.max(1, Math.ceil(pagesRef.current.length / 2));
      const next = Math.min(spreads - 1, Math.max(0, spreadRef.current + direction));
      if (next === spreadRef.current) return;
      lockedUntil = now + 280;
      spreadRef.current = next;
      setSpread(next);
    };
    const onKey = (event) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      if (editing) return;
      if (event.shiftKey || event.altKey || event.metaKey || event.ctrlKey) return;
      const tag = event.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      event.preventDefault();
      turn(event.key === "ArrowLeft" ? -1 : 1);
    };
    const onWheel = (event) => {
      if (!frame.contains(event.target)) return;
      if (!event.deltaY) return;
      event.preventDefault();
      turn(event.deltaY > 0 ? 1 : -1);
    };
    window.addEventListener("keydown", onKey);
    frame.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      window.removeEventListener("keydown", onKey);
      frame.removeEventListener("wheel", onWheel);
    };
  }, [editing]);

  const leftIndex = spread * 2;
  const rightIndex = spread * 2 + 1;
  const leftText = pages[leftIndex] || "";
  const rightText = pages[rightIndex] || "";
  const leftNumber = leftIndex < pages.length ? leftIndex + 1 : 0;
  const rightNumber = rightIndex < pages.length ? rightIndex + 1 : 0;

  const renderBody = (pageIndex, text, bodyRef) => {
    if (!editing) return <div className="notes-spread__body">{text}</div>;
    return (
      <div
        ref={bodyRef}
        className="notes-spread__body"
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-label={pageIndex % 2 === 0 ? "Page de gauche" : "Page de droite"}
        onInput={(event) => emitPage(pageIndex, event.currentTarget)}
      />
    );
  };

  return (
    <div
      className={typeface === "comic" ? "notes-spread notes-spread--comic" : "notes-spread"}
      style={{ fontSize: `${fontSize}px` }}
      aria-label="Note sur deux pages"
    >
      <div className="notes-spread__frame" ref={frameRef}>
        <div className="notes-spread__book">
          <div className="notes-spread__page" ref={leftPageRef}>
            {leftIndex === 0 ? (
              editing ? (
                <div
                  ref={titleRef}
                  className="notes-spread__title"
                  contentEditable
                  suppressContentEditableWarning
                  role="textbox"
                  aria-label="Titre de la note"
                  onKeyDown={(event) => {
                    if (event.key === "Enter") event.preventDefault();
                  }}
                  onInput={(event) => onTitleChange(plainText(event.currentTarget).replace(/\n/g, " "))}
                />
              ) : (
                <h2 className="notes-spread__title">{shownTitle}</h2>
              )
            ) : null}
            {renderBody(leftIndex, leftText, leftBodyRef)}
          </div>
          <div className="notes-spread__gap" aria-hidden="true">
            {editing ? <span className="notes-spread__gutter" /> : null}
          </div>
          <div className="notes-spread__page" ref={rightPageRef}>
            {renderBody(rightIndex, rightText, rightBodyRef)}
          </div>
        </div>
        {leftNumber ? <span className="notes-spread__num notes-spread__num--left">{leftNumber}</span> : null}
        {rightNumber ? <span className="notes-spread__num notes-spread__num--right">{rightNumber}</span> : null}
        <div className="notes-spread__sheet notes-spread__measure" ref={measureRef} aria-hidden="true" />
      </div>
    </div>
  );
}
